// One Case (`/me/cases/<id>`), and a new one (`/me/cases/new`) on the same
// screen: the title written in place, the state, the label, the Context,
// Related and the Chain.
//
// Promise:
// - A new Case is made once, when its title is left with Enter or the focus
//   leaves the screen, and never on Escape (Escape leaves without making it).
//   Made, the URL becomes the Case's (replacing, so Back does not return to an
//   empty form) and the screen is not made again: what was being written stays.
// - Every write shows at once; a refusal is one line under what was pressed.
// - The Context is written live (`CaseContext`): other windows and AI writes
//   show in it as they happen, and nothing typed is lost to them.
// - Which step is open in place is screen state (`useCasesScreen`), not URL.

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { Editor } from "@tiptap/core";
import { CASE_TITLE_MAX, type CaseDetail } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import { useEffect, useId, useRef, useState } from "react";
import { useApi } from "../app/context";
import { ApiError } from "../data/api";
import { apiErrorMessage, apiErrorWithLead } from "../data/errors";
import { newOptimisticId } from "../data/optimistic";
import { suggestionOpen } from "../documents/editor/suggest";
import { startTyping } from "../documents/editor/tap-to-type";
import type { LiveTransport } from "../documents/live";
import { LabelChips } from "../labels/chip";
import { LabelPickPopover, useLabelMenuItems } from "../labels/label-pick";
import { useLabelsOf } from "../labels/queries";
import { FavoriteStar, useFavoriteAction } from "../me/favorite-star";
import { useFormat } from "../me/profile";
import { useRecordVisitOnDwell } from "../me/visits";
import { AgentMemoDialog } from "../memos/memo-dialog";
import { copyLinkItem } from "../shell/copy-link";
import { useHeaderActions } from "../shell/header-actions";
import {
  DetailHeader,
  HEADER_ICON_BUTTON,
  HEADER_PILL,
  useWideMenuItem,
} from "../shell/list-detail";
import {
  menuActions,
  type ShellHeaderInput,
  useShellEmbedded,
  useShellHeader,
} from "../shell/outside/outside";
import { useSideView } from "../shell/side-view";
import { ErrorLine, Skeleton } from "../ui/bits";
import { confirmAction } from "../ui/confirm";
import { Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { MenuButton, type MenuItem } from "../ui/menu";
import { toast } from "../ui/toast";
import { useWorkspaceAction } from "../workspaces/pick";
import {
  AddStepModal,
  emptyDraft,
  type StepDraft,
  type StepKind,
} from "./add-step";
import { DetailBody, SECTION, SectionToggle, useSectionOpen } from "./bits";
import { Chain } from "./chain";
import { ChainGraph, ChainGraphButton } from "./chain-graph";
import { CaseContext } from "./context";
import { CaseFiles } from "./files";
import { PeekClose, useCasePeek } from "./peek-context";
import {
  type CasePatch,
  caseQuery,
  type StepAddInput,
  screenParents,
  useCreateCase,
  useDeleteCase,
  useUpdateCase,
} from "./queries";
import { Related } from "./related";
import { useCasesScreen, useOpened } from "./screen-state";

/**
 * A small pill button of a header (done, reopen, the label): the old web's
 * 11.5px, second-level text on the surface, in the pill's quiet line.
 */
export { HEADER_PILL };

/** `HEADER_PILL` folded to its icon (a narrow header): a 26px circle. */
export const HEADER_PILL_ICON =
  "inline-flex h-[26px] w-[26px] flex-none cursor-pointer items-center justify-center rounded-[13px] border border-pill-border bg-surface text-body-soft hover:border-field-strong hover:text-body";

const STEP_BUTTON =
  "flex-none cursor-pointer rounded-[11px] border border-pill-border bg-surface px-[9px] py-[1px] text-[11.5px] text-body-soft hover:border-field-strong hover:text-body";

interface Session {
  /** The path's id ("new" for a new Case). */
  param: string;
  key: string;
  /** The Case this session made (a new Case keeps its screen once made). */
  created: string | null;
}

const sessionFor = (param: string): Session => ({
  param,
  key: param === "new" ? `new:${newOptimisticId()}` : param,
  created: null,
});

/** The route's screen: one session per Case shown (a new one keeps its own once made). */
export function CaseRoute({ transport }: { transport: LiveTransport }) {
  const { caseId: param } = useParams({ strict: false }) as { caseId: string };
  const [session, setSession] = useState(() => sessionFor(param));
  // Another Case in the path starts a new session (a reset, not a copy): the
  // Case this session made keeps it, so its screen is not made again.
  if (session.param !== param)
    setSession(
      session.created === param ? { ...session, param } : sessionFor(param),
    );
  const caseId = param === "new" ? session.created : param;
  return (
    <CaseScreen
      key={session.key}
      caseId={caseId}
      transport={transport}
      onCreated={(id) => setSession((s) => ({ ...s, created: id }))}
    />
  );
}

export function CaseScreen({
  caseId,
  transport,
  onCreated,
}: {
  caseId: string | null;
  transport: LiveTransport;
  onCreated(id: string): void;
}) {
  const q = useQuery({
    ...caseQuery(useApi(), caseId ?? ""),
    enabled: caseId !== null,
  });
  if (caseId !== null && q.isError)
    return (
      <>
        <DetailHeader actions={<PeekClose />} />
        <DetailBody>
          <p className="text-[13px] text-muted">
            {q.error instanceof ApiError && q.error.status === 404
              ? m.cases_not_found()
              : apiErrorMessage(q.error, m.cases_load_failed())}
          </p>
        </DetailBody>
      </>
    );
  if (caseId !== null && !q.data)
    return (
      <>
        <DetailHeader actions={<PeekClose />} />
        <DetailBody>
          <Skeleton lines={[50, 80, 70, 40]} />
        </DetailBody>
      </>
    );
  return (
    <CaseView c={q.data ?? null} transport={transport} onCreated={onCreated} />
  );
}

/** Where a refusal is told: under the title, Related, the Chain, or one step's row. */
type ErrorAt = "head" | "related" | "chain" | `step:${string}`;

function CaseView({
  c,
  transport,
  onCreated,
}: {
  /** null: a new Case, not made yet. */
  c: CaseDetail | null;
  transport: LiveTransport;
  onCreated(id: string): void;
}) {
  const navigate = useNavigate();
  const screen = useCasesScreen();
  const opened = useOpened(c?.id ?? null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const caseReveal = opened?.reveal === true && opened.stepId === null;
  const { revealed } = screen;
  // frontend-development: not a fetch; a caret asked for a step just added is
  // used once: leaving the Case spends it, so going back to it does not jump.
  const focusUsed = useRef(screen.focusUsed);
  focusUsed.current = screen.focusUsed;
  const caseId = c?.id ?? null;
  useEffect(
    () => () => {
      if (caseId) focusUsed.current(caseId);
    },
    [caseId],
  );
  // frontend-development: not a fetch; the Case opened again from the list (its own row) shows from its top.
  useEffect(() => {
    if (!caseReveal) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    revealed();
  }, [caseReveal, revealed]);
  const [error, setErrorAt] = useState<{ at: ErrorAt; text: string } | null>(
    null,
  );
  const errorAt = (at: ErrorAt) =>
    error?.at === at ? <ErrorLine>{error.text}</ErrorLine> : null;
  const update = useUpdateCase();
  const write = (at: ErrorAt) => (patch: CasePatch, lead: string) => {
    if (!c) return;
    setErrorAt(null);
    update.mutate(
      { caseId: c.id, patch },
      {
        onError: (err) => setErrorAt({ at, text: apiErrorWithLead(err, lead) }),
      },
    );
  };
  /** An extension's write of a step (its own inputs), awaited by its row. */
  const writeStepAsync = (
    input: Readonly<Record<string, unknown>>,
    lead: string,
    stepId: string,
  ): Promise<unknown> => {
    if (!c) return Promise.reject(new Error("no case"));
    setErrorAt(null);
    return update
      .mutateAsync({ caseId: c.id, patch: input as CasePatch })
      .catch((err: unknown) => {
        setErrorAt({ at: `step:${stepId}`, text: apiErrorWithLead(err, lead) });
        throw err;
      });
  };
  const [graphOpen, setGraphOpen] = useState(false);
  useRecordVisitOnDwell("case", c?.id ?? null);

  // ---- a new Case ----
  const create = useCreateCase();
  const [title, setTitle] = useState("");
  // The Context's editor (Enter on a new title moves into it).
  const contextEditor = useRef<Editor | null>(null);
  const submitted = useRef(false);
  const leaving = useRef(false);
  const makeCase = (firstStep?: StepAddInput, draft?: StepDraft): boolean => {
    const name = title.trim();
    if (c || name === "" || submitted.current) return false;
    submitted.current = true;
    setErrorAt(null);
    create.mutate(
      // The Context written so far joins the Case's live copy once it exists.
      { title: name, ...(firstStep ? { first_step: firstStep } : {}) },
      {
        onSuccess: (made) => {
          onCreated(made.id);
          if (firstStep && made.steps[0])
            screen.openStep(made.id, made.steps[0].id, { focus: "body" });
          void navigate({
            to: "/me/cases/$caseId",
            params: { caseId: made.id },
            replace: true,
          });
        },
        onError: (err) => {
          submitted.current = false;
          const text = apiErrorMessage(err, m.cases_create_failed_kept());
          if (draft) setAdding({ draft, error: text });
          else setErrorAt({ at: "head", text });
        },
      },
    );
    return true;
  };
  const leaveNew = () => {
    leaving.current = true;
    void navigate({ to: "/me/cases" });
  };

  // ---- adding a step ----
  const [adding, setAdding] = useState<{
    draft: StepDraft;
    error: string | null;
  } | null>(null);
  const openAdd = (kind: StepKind) => {
    setErrorAt(null);
    setAdding({ draft: emptyDraft(kind), error: null });
  };
  const submitStep = (step: StepAddInput, draft: StepDraft): boolean => {
    if (!c) return makeCase(step, draft);
    const known = new Set(c.steps.map((s) => s.id));
    update.mutate(
      {
        caseId: c.id,
        patch: { add_steps: [{ ...step, parents: screenParents(c.steps) }] },
        view: { waitLabel: draft.person?.name },
      },
      {
        // The step just added opens with the caret in its body.
        onSuccess: (detail) => {
          const added = detail.steps.filter((s) => !known.has(s.id)).at(-1);
          if (added) screen.openStep(c.id, added.id, { focus: "body" });
        },
        onError: (err) =>
          setAdding({
            draft,
            error: apiErrorWithLead(
              err,
              m.cases_step_add_failed({ title: draft.title }),
            ),
          }),
      },
    );
    return true;
  };

  // ---- the Context ----
  const [relatedOpen, toggleRelated] = useSectionOpen("related");
  const relatedId = useId();
  const saveStepText = (
    stepId: string,
    field: "body" | "result",
    text: string,
  ): Promise<unknown> => {
    if (!c) return Promise.resolve();
    return update.mutateAsync({
      caseId: c.id,
      patch: {
        update_steps: [
          {
            id: stepId,
            ...(field === "body" ? { body: text } : { result: text || null }),
          },
        ],
      },
    });
  };

  return (
    <>
      {c ? <CaseHeader c={c} write={write("head")} /> : <DetailHeader />}
      <DetailBody scrollRef={scrollRef}>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: receives the focus and Escape of the fields inside (a new Case is made when the focus leaves it) */}
        <div
          onBlur={(e) => {
            if (c || leaving.current) return;
            if (e.currentTarget.contains(e.relatedTarget as Node | null))
              return;
            makeCase();
          }}
          // Caught on the way down: the field that has the focus blurs on
          // Escape, and that blur must already know the screen is being left.
          onKeyDownCapture={(e) => {
            if (
              c ||
              e.key !== "Escape" ||
              isImeComposing(e.nativeEvent) ||
              (e.target as Element).closest("[role=dialog]")
            )
              return;
            // An open `/` menu or `[[` list in the Context takes this Escape.
            const editor = contextEditor.current;
            if (
              editor?.view.dom.contains(e.target as Node) &&
              suggestionOpen(editor)
            )
              return;
            leaveNew();
          }}
        >
          <CaseTitle
            c={c}
            title={title}
            setTitle={setTitle}
            onEnterNew={() => {
              if (title.trim() === "") return;
              // Into the Context first: the next keystroke must land there.
              // The DOM focus now (the editor's focus command waits a frame,
              // and what is typed meanwhile went into the title). On a phone
              // the Context opens reading: writable first, in this keystroke,
              // or the focus does not move and the keyboard goes.
              const editor = contextEditor.current;
              if (editor) {
                startTyping(editor);
                editor.view.dom.focus();
                editor.commands.focus("end");
              }
              makeCase();
            }}
            write={write("head")}
          />
          {errorAt("head")}

          <div className={`${SECTION} mt-6`}>{m.cases_context()}</div>
          <CaseContext
            caseId={c?.id ?? null}
            note={c?.note ?? ""}
            transport={transport}
            onEditor={(e) => {
              contextEditor.current = e;
            }}
          />
          {c && <CaseFiles caseId={c.id} files={c.files} />}

          {c && (
            <>
              <SectionToggle
                label={m.cases_related()}
                open={relatedOpen}
                onToggle={toggleRelated}
                controls={relatedId}
              />
              <div id={relatedId} hidden={!relatedOpen}>
                <Related c={c} write={write("related")} />
                {errorAt("related")}
              </div>
            </>
          )}

          <div className="mt-7 mb-1 flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
            <span className="text-[11px] tracking-[0.04em] text-faint">
              {m.cases_chain()}
            </span>
            <button
              type="button"
              className={STEP_BUTTON}
              onClick={() => openAdd("action")}
            >
              {m.cases_add_action_button()}
            </button>
            <button
              type="button"
              className={STEP_BUTTON}
              onClick={() => openAdd("waiting")}
            >
              {m.cases_add_waiting_button()}
            </button>
            {/* The diagram only once there are steps to tie together. */}
            {c && c.steps.length >= 2 && (
              <span className="ml-auto flex">
                <ChainGraphButton onClick={() => setGraphOpen(true)} />
              </span>
            )}
          </div>
          {c && graphOpen && (
            <ChainGraph
              c={c}
              onClose={() => setGraphOpen(false)}
              // Closes and opens that step in place, brought into view.
              onSelect={(stepId) => {
                setGraphOpen(false);
                screen.openStep(c.id, stepId, { reveal: true });
              }}
            />
          )}
          {errorAt("chain")}
          {c ? (
            <Chain
              c={c}
              opened={opened}
              onOpen={(stepId) => screen.openStep(c.id, stepId)}
              onRevealed={revealed}
              write={(patch, lead, stepId) =>
                write(stepId ? `step:${stepId}` : "chain")(patch, lead)
              }
              writeAsync={writeStepAsync}
              stepError={(stepId) => errorAt(`step:${stepId}`)}
              saveText={saveStepText}
            />
          ) : (
            <p className="text-[12.5px] text-faint">{m.cases_chain_empty()}</p>
          )}
          {adding && (
            <AddStepModal
              initial={adding.draft}
              error={adding.error}
              blocked={
                !c && title.trim() === ""
                  ? m.cases_add_step_needs_title()
                  : null
              }
              onClose={() => setAdding(null)}
              onSubmit={submitStep}
            />
          )}
        </div>
      </DetailBody>
    </>
  );
}

function CaseTitle({
  c,
  title,
  setTitle,
  onEnterNew,
  write,
}: {
  c: CaseDetail | null;
  title: string;
  setTitle(title: string): void;
  onEnterNew(): void;
  write(patch: CasePatch, lead: string): void;
}) {
  // While writing, what is being written; otherwise the Case's title as it is now.
  const [draft, setDraft] = useState<string | null>(null);
  const cancel = useRef(false);
  const TEXT =
    "plain-field block w-full resize-none border-none bg-transparent p-0 text-[21px] leading-[1.5] tracking-[-0.01em] text-fg outline-none [field-sizing:content]";
  if (!c)
    return (
      <textarea
        rows={1}
        // biome-ignore lint/a11y/noAutofocus: a new Case was asked for, to be written now
        autoFocus
        value={title}
        maxLength={CASE_TITLE_MAX}
        aria-label={m.cases_case_title()}
        placeholder={m.cases_title_placeholder()}
        onChange={(e) => setTitle(e.target.value.replace(/\n/g, " "))}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || isImeComposing(e.nativeEvent)) return;
          e.preventDefault();
          onEnterNew();
        }}
        className={TEXT}
      />
    );
  return (
    <textarea
      rows={1}
      value={draft ?? c.title}
      maxLength={CASE_TITLE_MAX}
      aria-label={m.cases_case_title()}
      onFocus={() => setDraft(c.title)}
      onChange={(e) => setDraft(e.target.value.replace(/\n/g, " "))}
      onKeyDown={(e) => {
        if (isImeComposing(e.nativeEvent)) return;
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          cancel.current = true;
          e.currentTarget.blur();
        }
      }}
      onBlur={() => {
        const next = (draft ?? "").trim();
        setDraft(null);
        if (cancel.current) {
          cancel.current = false;
          return;
        }
        if (next && next !== c.title)
          write({ title: next }, m.cases_title_failed());
      }}
      className={TEXT}
    />
  );
}

function CaseHeader({
  c,
  write,
}: {
  c: CaseDetail;
  write(patch: CasePatch, lead: string): void;
}) {
  const navigate = useNavigate();
  const peek = useCasePeek();
  const format = useFormat();
  const [memoOpen, setMemoOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const side = useSideView() !== null;
  const toggleLabel = useToggleCaseLabel(c, write);
  const labelNames = useLabelsOf(c.labelIds).map((l) => l.name);
  // A host that draws the bar draws the labels' pick as the chip's menu (contract 29).
  const labelItems = useLabelMenuItems({
    enabled: useShellEmbedded() && !peek && !side,
    selected: c.labelIds,
    onPick: toggleLabel,
  });
  const remove = useDeleteCase((err, input) =>
    toast(apiErrorWithLead(err, m.cases_delete_failed({ title: input.title }))),
  );
  const wide = useWideMenuItem();
  const others = useHeaderActions("case", c.id, c.ext);
  const middle: MenuItem[] = [
    ...others.menuItems,
    ...(c.agentMemo.body.trim() !== "" || c.agentMemo.logCount > 0
      ? [
          {
            id: "memo",
            label: m.cases_agent_memo(),
            onSelect: () => setMemoOpen(true),
          },
        ]
      : []),
    ...(c.status === "open"
      ? [
          {
            id: "drop",
            label: m.cases_drop(),
            onSelect: () =>
              write({ status: "dropped" }, m.cases_status_failed()),
          },
        ]
      : []),
  ];
  const items: (MenuItem | "separator")[] = [
    ...(wide ? [wide] : []),
    copyLinkItem(`/me/cases/${c.id}`),
    ...(middle.length > 0 ? ["separator" as const, ...middle] : []),
    "separator",
    {
      id: "delete",
      label: m.common_delete(),
      danger: true,
      onSelect: async () => {
        const ok = await confirmAction({
          title: m.cases_delete_title({ title: c.title }),
          body: m.cases_delete_body(),
          action: m.common_delete(),
          danger: true,
        });
        if (!ok) return;
        remove.mutate({ caseId: c.id, title: c.title });
        if (peek) peek.close();
        else void navigate({ to: "/me/cases" });
      },
    },
  ];
  // Done or reopen: a pill at the right end, or the menu's first row when the
  // header is narrow (the old web's phone did the same).
  const toggle =
    c.status === "open"
      ? {
          id: "status",
          label: m.cases_done(),
          onSelect: () => write({ status: "done" }, m.cases_status_failed()),
        }
      : {
          id: "status",
          label: m.cases_reopen(),
          onSelect: () => write({ status: "open" }, m.cases_status_failed()),
        };
  const workspace = useWorkspaceAction({ kind: "case", id: c.id }, [
    ...(c.workspaceId ? [c.workspaceId] : []),
  ]);
  const star = useFavoriteAction({
    kind: "case",
    targetId: c.id,
    title: c.title,
  });
  // The header as data: the web's row draws it (each piece by its own
  // `render`), and a frame drawn outside gets the same (shell/outside), but
  // not from a peek or side view, which sit beside another screen.
  const header: ShellHeaderInput = {
    title: c.title,
    back: true,
    chips: [
      {
        id: "label",
        label: labelNames.join(", ") || m.cases_label(),
        icon: "label",
        onPress: () => setLabelOpen(true),
        ...(labelItems ? { items: labelItems } : {}),
        render: (narrow) => (
          <LabelPill
            c={c}
            write={write}
            narrow={narrow}
            open={labelOpen}
            setOpen={setLabelOpen}
          />
        ),
      },
    ],
    ...(c.status !== "open"
      ? {
          note: [
            c.status === "done"
              ? m.cases_status_done()
              : m.cases_status_dropped(),
            c.closedAt ? format.day(c.closedAt) : null,
          ]
            .filter(Boolean)
            .join(" · "),
        }
      : {}),
    actions: [
      workspace,
      {
        id: "status",
        label: toggle.label,
        style: "pill",
        // A bar that shows it as an icon: a checkbox, checked once done (the
        // person 2026-10-31), its own mark once dropped.
        icon: c.status === "dropped" ? "dropped" : "checkbox",
        on: c.status === "done",
        onPress: toggle.onSelect,
        // Narrow: the menu's first row instead.
        render: (narrow) =>
          narrow ? null : (
            <button
              type="button"
              className={HEADER_PILL}
              onClick={toggle.onSelect}
            >
              {toggle.label}
            </button>
          ),
      },
      ...others.actions,
      {
        ...star,
        render: () => (
          <FavoriteStar kind="case" targetId={c.id} title={c.title} />
        ),
      },
      {
        id: "more",
        label: m.cases_menu({ title: c.title }),
        icon: "more",
        items: menuActions(items),
        render: (narrow) => (
          <MenuButton
            label={m.cases_menu({ title: c.title })}
            align="end"
            className={HEADER_ICON_BUTTON}
            items={narrow ? [toggle, "separator", ...items] : items}
          >
            <Icon kind="more" size={15} />
          </MenuButton>
        ),
      },
    ],
  };
  useShellHeader(peek || side ? null : header);
  return (
    <>
      <DetailHeader header={header} end={<PeekClose />} />
      <AgentMemoDialog
        kind="case"
        id={c.id}
        open={memoOpen}
        onClose={() => setMemoOpen(false)}
      />
    </>
  );
}

/** The toggle of one label on the Case (on: taken off; off: put on). */
function useToggleCaseLabel(
  c: CaseDetail,
  write: (patch: CasePatch, lead: string) => void,
) {
  return (label: { id: string; name: string }) =>
    write(
      c.labelIds.includes(label.id)
        ? { remove_labels: [label.id] }
        : { add_labels: [label.id] },
      m.cases_label_failed(),
    );
}

function LabelPill({
  c,
  write,
  narrow,
  open,
  setOpen,
}: {
  c: CaseDetail;
  write(patch: CasePatch, lead: string): void;
  /** The header is narrow: only the icon (the name is read aloud). */
  narrow: boolean;
  /** Kept by the header: a bar drawn outside opens it too (header data). */
  open: boolean;
  setOpen(open: boolean | ((open: boolean) => boolean)): void;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const labels = useLabelsOf(c.labelIds);
  const toggle = useToggleCaseLabel(c, write);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          labels.length > 0
            ? m.cases_label_change({
                name: labels.map((l) => l.name).join(", "),
              })
            : m.cases_label_set()
        }
        onClick={() => setOpen((v) => !v)}
        className={`${narrow ? HEADER_PILL_ICON : HEADER_PILL} min-w-0 gap-1.5 ${labels.length > 0 ? "" : "text-muted"}`}
      >
        {labels.length === 0 || narrow ? <Icon kind="label" size={13} /> : null}
        {!narrow &&
          (labels.length > 0 ? (
            <LabelChips ids={c.labelIds} wrap={false} />
          ) : (
            <span className="min-w-0 truncate">{m.cases_label()}</span>
          ))}
      </button>
      {open && (
        <LabelPickPopover
          anchor={anchor}
          label={m.cases_label()}
          multiple
          selected={c.labelIds}
          onClose={() => setOpen(false)}
          onPick={toggle}
        />
      )}
    </>
  );
}
